/**
 * Batch Service
 * Service for managing batch allocation operations
 *
 * @author SAUBHAGYA Development Team
 * @since Order Enhancement with Batch Selection
 */

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface BatchAvailability {
  batchId: number;
  batchCode: string;
  productId: number;
  productName: string;
  availableQuantityKg: number;
  unitPrice: number;
  expiryDate?: string;
}

export interface DungProductWithBatches {
  productId: number;
  productName: string;
  description?: string;
  unit: string;
  batches: BatchAvailability[];
}

export interface BatchSelectionRequest {
  batchId: number;
  dungProductId: number;
  quantity: number;
}

// Alias for backwards compatibility
export type BatchSelection = BatchSelectionRequest;

export interface CreateOrderWithBatchRequest {
  buyerId: number;
  batchSelections: BatchSelectionRequest[];
  deliveryAddress?: string;
  specialInstructions?: string;
  expectedDeliveryDate?: string;
}

// ============================================
// CONFIGURATION
// ============================================

const COMMERCE_SERVICE_BASE_URL = import.meta.env.PROD
  ? (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service')
  : '/commerce-service';
const ORDER_API_PATH = '/api/v1/orders';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  const authHeader = token
    ? token.startsWith('Bearer ') ? token : `Bearer ${token}`
    : undefined;
  return {
    'Content-Type': 'application/json',
    ...(authHeader && { Authorization: authHeader }),
  };
}

// ============================================
// API RESPONSE TYPE
// ============================================

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

// ============================================
// ERROR HANDLING
// ============================================

export class BatchApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'BatchApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // Ignore JSON parse errors
    }
    throw new BatchApiError(errorMessage, response.status);
  }

  const data = await response.json();
  if (data.success === false) {
    throw new BatchApiError(data.message || 'Request failed');
  }
  return data.data !== undefined ? data.data : data;
}

// ============================================
// MANUFACTURING UNIT ENDPOINTS (Order Creation Flow)
// ============================================

/**
 * Manufacturing Unit type for order creation flow
 */
export interface ManufacturingUnitFromDB {
  id: number;
  name: string;
  location?: string;
  isActive?: boolean;
}

/**
 * Product type for order creation flow
 */
export interface ProductFromDB {
  id: number;
  productName: string;
  description?: string;
  category?: string;
  price?: number;
  unit?: string;
}

/**
 * Batch type for order creation flow
 */
export interface BatchFromDB {
  id: number;
  batchCode: string;
  status: string;
  availableQuantityKg: number;
  reservedQuantityKg?: number;
  outputQuantityKg?: number;
  productId?: number;
  productName?: string;
  price?: number;
  manufacturingUnitId?: number;
  manufacturingUnitName?: string;
  createdAt?: string;
  notes?: string;
}

/**
 * Get all manufacturing units from mfg_manufacturing_unit table
 * Used for the first dropdown in order creation
 */
export async function getManufacturingUnitsFromDB(): Promise<ManufacturingUnitFromDB[]> {
  const url = `${COMMERCE_SERVICE_BASE_URL}/api/v1/mfg-production-batches/manufacturing-units`;
  console.log('[BatchService] Fetching manufacturing units from mfg_manufacturing_unit table:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  const result = await handleResponse<ManufacturingUnitFromDB[] | { data: ManufacturingUnitFromDB[] }>(response);
  // Handle both direct array response and wrapped response
  return Array.isArray(result) ? result : (result as { data: ManufacturingUnitFromDB[] }).data || [];
}

/**
 * Get products available for a specific manufacturing unit
 * Returns products that have READY_FOR_SALE batches with available quantity
 */
export async function getProductsByManufacturingUnit(unitId: number): Promise<ProductFromDB[]> {
  const url = `${COMMERCE_SERVICE_BASE_URL}/api/v1/mfg-production-batches/products-by-unit?unitId=${unitId}`;
  console.log('[BatchService] Fetching products for manufacturing unit:', unitId);

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  const result = await handleResponse<ProductFromDB[] | { data: ProductFromDB[] }>(response);
  // Handle both direct array response and wrapped response
  return Array.isArray(result) ? result : (result as { data: ProductFromDB[] }).data || [];
}

/**
 * Get available batches for a specific manufacturing unit and product
 * Returns batches with READY_FOR_SALE status and available quantity > 0
 */
export async function getBatchesByUnitAndProduct(unitId: number, productId: number): Promise<BatchFromDB[]> {
  const url = `${COMMERCE_SERVICE_BASE_URL}/api/v1/mfg-production-batches/batches-by-unit-and-product?unitId=${unitId}&productId=${productId}`;
  console.log('[BatchService] Fetching batches for unit:', unitId, 'and product:', productId);

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  const result = await handleResponse<BatchFromDB[] | { data: BatchFromDB[] }>(response);
  // Handle both direct array response and wrapped response
  return Array.isArray(result) ? result : (result as { data: BatchFromDB[] }).data || [];
}

// ============================================
// BATCH AVAILABILITY ENDPOINTS (Legacy)
// ============================================

/**
 * Get all products with their available batches
 */
export async function getProductsWithBatches(): Promise<DungProductWithBatches[]> {
  const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/products/with-batches`;
  console.log('[BatchService] Fetching products with batches:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  return handleResponse<DungProductWithBatches[]>(response);
}

/**
 * Get available batches for a specific dung product
 */
export async function getAvailableBatches(dungProductId: number): Promise<BatchAvailability[]> {
  const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/batches/available?dungProductId=${dungProductId}`;
  console.log('[BatchService] Fetching available batches for product:', dungProductId);

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  return handleResponse<BatchAvailability[]>(response);
}

// ============================================
// ORDER WITH BATCH ALLOCATION ENDPOINTS
// ============================================

/**
 * Create order with batch allocation
 */
export async function createOrderWithBatches(
  request: CreateOrderWithBatchRequest
): Promise<unknown> {
  const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/with-batch-allocation`;
  console.log('[BatchService] Creating order with batch allocation:', request);

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });

  return handleResponse(response);
}

// Export all functions as named exports
export const batchService = {
  // New order creation flow endpoints (uses mfg_manufacturing_unit table)
  getManufacturingUnitsFromDB,
  getProductsByManufacturingUnit,
  getBatchesByUnitAndProduct,
  // Legacy endpoints
  getProductsWithBatches,
  getAvailableBatches,
  createOrderWithBatches,
};

export default batchService;
